import { HERO_RANK_BANDS } from "@/components/shared/HeroRankColorKey";
import { SiblingRankBadge } from "@/components/shared/SiblingRankBadge";
import { isKeywordMetricPending, KEYWORD_PENDING_LABEL } from "@/utils/keywordPendingDisplay";

// Re-exported because this module was the bands' home before they were shared, and the
// Keywords-tab code reads the bands and the classes together. One definition, two import paths.
export { HERO_RANK_BANDS };

interface RankingCellProps {
  rank: number | null | undefined;
  isHeroColumn?: boolean;
  siblingAsin?: string | null;
}

/**
 * Background-only classes for the four states the pill below paints, so the Hero Rank column's
 * colour key draws its swatches from the thing it describes instead of naming a colour twice.
 * `HeroRankPill` does the same for the A/B Keyword Bank picker; both hand theirs to the one
 * `HeroRankColorKey`, which owns the band numbers and the record of why the two palettes differ.
 */
export const HERO_RANK_BAND_CLASSES = {
  top: "bg-status-success-soft",
  mid: "bg-status-warning-soft",
  terminal: "bg-status-error-soft",
  unranked: "bg-muted",
} as const;

function getRankPillClasses(rank: number | null | undefined) {
  const base =
    "inline-flex items-center justify-center gap-0.5 rounded-md px-2 py-1 text-xs tabular-nums text-foreground min-w-[2.25rem]";
  if (rank === null || rank === undefined) return `${base} ${HERO_RANK_BAND_CLASSES.unranked}`;
  if (isKeywordMetricPending(rank)) return `${base} bg-muted/40 text-muted-foreground`;
  if (rank <= HERO_RANK_BANDS.greenMax) return `${base} ${HERO_RANK_BAND_CLASSES.top}`;
  if (rank <= HERO_RANK_BANDS.yellowMax) return `${base} ${HERO_RANK_BAND_CLASSES.mid}`;
  return `${base} ${HERO_RANK_BAND_CLASSES.terminal}`;
}

export function RankingCell({ rank, siblingAsin }: RankingCellProps) {
  const displayValue =
    rank === null || rank === undefined
      ? "N/A"
      : isKeywordMetricPending(rank)
        ? KEYWORD_PENDING_LABEL
        : rank;

  const showSiblingBadge =
    typeof siblingAsin === "string" &&
    siblingAsin.trim() !== "" &&
    typeof rank === "number" &&
    rank >= 1 &&
    !isKeywordMetricPending(rank);

  return (
    <div className="text-center text-sm py-2">
      <span className={getRankPillClasses(rank)}>
        {displayValue}
        {showSiblingBadge && <SiblingRankBadge asin={siblingAsin} className="-mr-1" />}
      </span>
    </div>
  );
}
